import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { toast } from 'sonner'
import type { QueryClient } from '@tanstack/react-query'
import type { TExhibitorWithEntries } from '@schemas'
import type { ExhibitorEntrySummary } from '@/pages/checkin/types'
import { apiClient } from '@/lib/apiClient'
import { parseApiErrorMessage } from '@/lib/api-errors'

type DeleteEntryMutation = {
  mutateAsync: (entryId: string) => Promise<void>
}

type IntakeActions = {
  removeCheckedInExhibit: (entryId?: string | null, exhibitId?: string | null) => void
}

type UseDeleteEntryActionParams = {
  selectedExhibitor: TExhibitorWithEntries | null
  setSelectedExhibitor: Dispatch<SetStateAction<TExhibitorWithEntries | null>>
  currentShowId: string | null
  deleteEntry: DeleteEntryMutation
  intake: IntakeActions
  queryClient: QueryClient | null
}

export function useDeleteEntryAction({
  selectedExhibitor,
  setSelectedExhibitor,
  currentShowId,
  deleteEntry,
  intake,
  queryClient,
}: UseDeleteEntryActionParams) {
  const [pendingId, setPendingId] = useState<string | null>(null)

  const canDeleteEntries = !selectedExhibitor?.finalizedAt
  const deleteDisabledReason = canDeleteEntries
    ? undefined
    : 'Entrant is finalized and entries cannot be deleted.'

  const handleDeleteEntry = useCallback(
    async (entry: ExhibitorEntrySummary) => {
      if (!entry?.id) return
      if (!selectedExhibitor) {
        toast.error('Select an entrant before deleting entries.')
        return
      }

      if (selectedExhibitor.finalizedAt) {
        toast.error('This entrant is finalized. Entries cannot be deleted.')
        return
      }

      const confirmed = window.confirm(
        'This will permanently remove the entry and retire its Exhibit ID. Continue?'
      )
      if (!confirmed) return

      let requireLabelWarning = false
      try {
        const detail = await apiClient.getEntry(entry.id)
        const labelState = detail.entry?.exhibit?.labelState
        requireLabelWarning = labelState === 'printed'
      } catch {
        requireLabelWarning = false
      }

      if (requireLabelWarning) {
        const secondConfirm = window.confirm(
          'A label for this entry was already printed. Delete the entry and retire the Exhibit ID?'
        )
        if (!secondConfirm) return
      }

      setPendingId(entry.id)
      try {
        await deleteEntry.mutateAsync(entry.id)

        setSelectedExhibitor(prev => {
          if (!prev) return prev
          if (!Array.isArray(prev.entries)) {
            return prev
          }
          return {
            ...prev,
            entries: prev.entries.filter(item => item?.id !== entry.id),
          }
        })

        if (queryClient && currentShowId) {
          queryClient.setQueriesData<TExhibitorWithEntries[]>(
            {
              predicate: query => {
                const key = query.queryKey
                if (!Array.isArray(key) || key[0] !== 'exhibitors') return false
                const params = key[1]
                return (
                  params &&
                  typeof params === 'object' &&
                  (params as { showId?: string | null }).showId === currentShowId
                )
              },
            },
            current => {
              if (!current) return current
              return current.map(item =>
                item.id === selectedExhibitor.id
                  ? {
                      ...item,
                      entries: (item.entries ?? []).filter(itemEntry => itemEntry?.id !== entry.id),
                    }
                  : item
              )
            }
          )
        }

        await intake.removeCheckedInExhibit(entry.id, entry.exhibitId ?? null)

        toast.success('Entry deleted. Exhibit ID retired.')
      } catch (error) {
        const message = parseApiErrorMessage(error)
        toast.error(message || 'Unable to delete entry')
      } finally {
        setPendingId(current => (current === entry.id ? null : current))
      }
    },
    [currentShowId, deleteEntry, intake, queryClient, selectedExhibitor, setSelectedExhibitor]
  )

  return {
    handleDeleteEntry,
    deleteEntryPendingId: pendingId,
    canDeleteEntries,
    deleteDisabledReason,
  }
}
